import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import playerService from "./playerService";
import { setAlertError, setAlertSuccess } from "../alert/alertSlice";

const initialState = {
  players: [],
  player: null,
  isLoading: false,
  isSuccess: false,
  isError: false,
  message: "",
};

export const getAllPlayers = createAsyncThunk(
  "player/getAllPlayers",
  async (_, thunkAPI) => {
    try {
      const response = await playerService.getAllPlayers();
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err =
        errData?.message || error.message || "Failed to fetch players";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  }
);

export const getPlayerById = createAsyncThunk(
  "player/getPlayerById",
  async (playerId, thunkAPI) => {
    try {
      const response = await playerService.getPlayerById(playerId);
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err = errData?.message || error.message || "Failed to fetch player";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  }
);

export const getPlayerByBSANumber = createAsyncThunk(
  "player/getPlayerByBSANumber",
  async (bsaNumber, thunkAPI) => {
    try {
      const response = await playerService.getPlayerByBSANumber(bsaNumber);
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err =
        errData?.message ||
        error.message ||
        "Failed to fetch player by BSA number";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  }
);

export const createPlayer = createAsyncThunk(
  "player/createPlayer",
  async (playerData, thunkAPI) => {
    try {
      const response = await playerService.createPlayer(playerData);
      thunkAPI.dispatch(setAlertSuccess("Player created successfully"));
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err =
        errData?.message || error.message || "Failed to create player";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  }
);

export const updatePlayer = createAsyncThunk(
  "player/updatePlayer",
  async ({ playerId, playerData }, thunkAPI) => {
    try {
      const response = await playerService.updatePlayer(playerId, playerData);
      thunkAPI.dispatch(setAlertSuccess("Player updated successfully"));
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err =
        errData?.message || error.message || "Failed to update player";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  }
);

export const deletePlayer = createAsyncThunk(
  "player/deletePlayer",
  async (playerId, thunkAPI) => {
    try {
      const response = await playerService.deletePlayer(playerId);
      thunkAPI.dispatch(setAlertSuccess("Player deleted successfully"));
      return response;
    } catch (error) {
      const errData = error.response?.data || null;
      const err =
        errData?.message || error.message || "Failed to delete player";
      thunkAPI.dispatch(setAlertError(err));
      return thunkAPI.rejectWithValue(errData);
    }
  }
);

const playerSlice = createSlice({
  name: "player",
  initialState,
  reducers: {
    reset: (state) => {
      state.players = [];
      state.player = null;
      state.isLoading = false;
      state.isSuccess = false;
      state.isError = false;
      state.message = "";
    },
  },
  extraReducers: (builder) => {
    builder
      // Get All Players
      .addCase(getAllPlayers.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getAllPlayers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.players = action.payload.data;
      })
      .addCase(getAllPlayers.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Get Player By ID
      .addCase(getPlayerById.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getPlayerById.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.player = action.payload.data;
      })
      .addCase(getPlayerById.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Get Player By BSA Number
      .addCase(getPlayerByBSANumber.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(getPlayerByBSANumber.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.player = action.payload.data;
      })
      .addCase(getPlayerByBSANumber.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Create Player
      .addCase(createPlayer.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(createPlayer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.players.push(action.payload.data);
        state.player = action.payload.data;
      })
      .addCase(createPlayer.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Update Player
      .addCase(updatePlayer.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(updatePlayer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        const index = state.players.findIndex(
          (p) => p.id === action.payload.id
        );
        if (index !== -1) {
          state.players[index] = action.payload;
        }
        state.player = action.payload;
      })
      .addCase(updatePlayer.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      })
      // Delete Player
      .addCase(deletePlayer.pending, (state) => {
        state.isLoading = true;
      })
      .addCase(deletePlayer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.isSuccess = true;
        state.players = state.players.filter((p) => p.id !== action.payload.id);
        state.player = null;
      })
      .addCase(deletePlayer.rejected, (state, action) => {
        state.isLoading = false;
        state.isError = true;
        state.message = action.payload;
      });
  },
});

export const { reset } = playerSlice.actions;
export default playerSlice.reducer;
